/// <amd-module name="Build/Common/Library/Components/RetentionPanel" />
import { RetentionLeaseData } from "Build/Client/Contracts/DataProvider";
import { BuildPersona } from "Build/Common/Library/Components/BuildPersona";
import { getAvatarUrl } from "Build/Common/Library/Identity";
import * as Resources from "Build/Common/Library/Resources";
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import * as StringUtil from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { TitleSize } from "VSSUI/Header";
import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader } from "VSSUI/Panel";
import { ITableColumn, SimpleTableCell, Table, TableColumnStyle, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";

export interface RetentionPanelProps {
    canDeleteLeases: boolean;
    onDelete: (ids: number[]) => void;
    onDismiss: () => void;
    retentionLeases: IReadonlyObservableArray<RetentionLeaseData>;
}

export class RetentionPanel extends VssComponent<RetentionPanelProps> {
    private _projectId: string;

    constructor(props: RetentionPanelProps, context: IVssComponentContext) {
        super(props, context);

        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (!!tfsPageData && !!tfsPageData.project) {
            this._projectId = tfsPageData.project.id;
        }
    }

    public render() {
        return (
            <CustomPanel onDismiss={this.props.onDismiss}>
                <PanelHeader
                    titleProps={{ text: Resources.RetentionLeasesPanelHeading, size: TitleSize.Large }}
                    description={Resources.RetentionPanelDescription}
                    onDismiss={this.props.onDismiss}
                />
                <PanelContent className="no-padding">
                    <div className="custom-scrollbar flex-column flex-grow h-scroll-hidden">
                        <div className="h-scroll-hidden">
                            <Table
                                ariaLabel={Resources.RetentionTableName}
                                columns={this._columns}
                                itemProvider={this.props.retentionLeases}
                                selectableText={true}
                                rowHeight={60}
                            />
                        </div>
                        <Observer leases={this.props.retentionLeases}>
                            {(props: { leases: RetentionLeaseData[] }) =>
                                props.leases.length === 0 ? (
                                    <span className="no-leases-span font-size-ms secondary-text">{Resources.RunNotRetained}</span>
                                ) : null
                            }
                        </Observer>
                    </div>
                </PanelContent>
                <Observer leases={this.props.retentionLeases}>
                    {(props: { leases: RetentionLeaseData[] }) => {
                        const buildSettingsRoute = routeUrl(this.context.pageContext, "ms.vss-build-web.build-settings-route", {
                            projectId: this._projectId
                        });
                        return (
                            <PanelFooter className="flex-row justify-space-between">
                                <Link className="a-link" href={buildSettingsRoute} target="_blank">
                                    {Resources.ProjectRetentionSettingsLink}
                                </Link>
                                <ButtonGroup>
                                    <Button text={Resources.ClosePanelButton} onClick={this.props.onDismiss} />
                                    <Button
                                        text={Resources.DeleteRetentionLeases}
                                        disabled={props.leases.length === 0}
                                        onClick={() => {
                                            let ids: number[] = this.props.retentionLeases.value.map(l => l.id);
                                            this.props.onDelete(ids);
                                        }}
                                        primary={true}
                                    />
                                </ButtonGroup>
                            </PanelFooter>
                        );
                    }}
                </Observer>
            </CustomPanel>
        );
    }

    private _renderOwnerCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RetentionLeaseData>,
        leaseData: RetentionLeaseData
    ): JSX.Element => {
        const daysToExpiration = Math.floor((leaseData.validUntil.getTime() - Date.now()) / (1000 * 3600 * 24));

        let icon: JSX.Element;
        let iconClassName = "lease-owner-icon";
        let primaryText = leaseData.ownerDisplayName;
        let secondaryText: string = "";
        let validUntil = daysToExpiration > 36500 ? Resources.forever : StringUtil.format(Resources.until, leaseData.validUntil.toLocaleDateString());
        if (StringUtil.equals(leaseData.leaseType, UserLeaseIdentifier, true) && leaseData.leaseOwnerIdentityRef) {
            let ownerIdRef = leaseData.leaseOwnerIdentityRef;
            icon = <BuildPersona size="medium" displayName={ownerIdRef.displayName} imageUrl={this._getAvatarUrl(ownerIdRef)} />;
            secondaryText = Resources.RetainedByUserSecondaryText + validUntil;
        } else if (StringUtil.equals(leaseData.leaseType, BranchLeaseIdentifier, true)) {
            icon = <Icon className={iconClassName} iconName="OpenSource" size={IconSize.medium} />;
            primaryText = Resources.RetainedByBranchPolicyPrimaryText;
            secondaryText = StringUtil.format(Resources.RetainedByBranchPolicySecondaryText, leaseData.ownerDisplayName) + validUntil;
        } else if (StringUtil.equals(leaseData.leaseType, BuildLeaseIdentifier, true)) {
            icon = <Icon className={iconClassName} iconName="Rocket" size={IconSize.medium} />;
            secondaryText = Resources.RetainedByPipelineResource + validUntil;
        } else if (StringUtil.equals(leaseData.leaseType, RMLeaseIdentifier, true)) {
            icon = <Icon className={iconClassName} iconName="Rocket" size={IconSize.medium} />;
            secondaryText = Resources.RetainedByReleaseSecondaryText + validUntil;
        } else if (StringUtil.equals(leaseData.leaseType, PipelineLeaseIdentifier, true)) {
            icon = <Icon className={iconClassName} iconName="Build" size={IconSize.medium} />;
            primaryText = Resources.RetainedByPipelineRetentionPolicyPrimaryText;
            secondaryText = Resources.RetainedByPipelineRetentionPolicySecondaryText + validUntil;
        } else {
            icon = <Icon className={iconClassName} iconName="Contact" size={IconSize.medium} />;
            secondaryText = validUntil;
        }

        const iconSpan = <span className="padding-right-8">{icon}</span>;

        const firstLine = (
            <Tooltip overflowOnly={true} text={primaryText}>
                <span className="text-ellipsis">{primaryText}</span>
            </Tooltip>
        );

        const secondLine = <span className="font-size secondary-text">{secondaryText}</span>;

        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                iconProps={{ render: () => iconSpan }}
                key={"col-" + columnIndex + " row-" + rowIndex}
                line1={firstLine}
                line2={<span className="font-size secondary-text">{secondLine}</span>}
            />
        );
    };

    private _renderDeleteCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RetentionLeaseData>,
        tableItem: RetentionLeaseData
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="scroll-hidden">
                <Button
                    ariaLabel={Resources.DeleteRetentionLeaseMessage}
                    className="bolt-list-cell-content-reveal"
                    disabled={!this.props.canDeleteLeases}
                    iconProps={{ iconName: "Delete" }}
                    onClick={event => {
                        this.props.onDelete([tableItem.id]);
                        event.preventDefault();
                    }}
                    subtle={true}
                    tooltipProps={{ text: Resources.DeleteRetentionLeaseMessage }}
                />
            </SimpleTableCell>
        );
    };

    private _getAvatarUrl = (requestedFor: IdentityRef): string => {
        if (requestedFor) {
            if (requestedFor.imageUrl) {
                return requestedFor.imageUrl;
            }

            const avatarUrl = getAvatarUrl(requestedFor);
            if (avatarUrl) {
                return avatarUrl;
            }

            if (requestedFor.id) {
                return getIdentityImageUrl(this.context.pageContext, requestedFor.id);
            }
        }

        return "";
    };

    private _columns: ITableColumn<RetentionLeaseData>[] = [
        {
            id: "lease-owner",
            width: new ObservableValue(413),
            name: Resources.LeaseOwnerColumnHeader,
            renderCell: this._renderOwnerCell,
            columnStyle: TableColumnStyle.Primary
        },
        {
            id: "delete-lease",
            width: new ObservableValue(51),
            renderCell: this._renderDeleteCell,
            columnStyle: TableColumnStyle.Secondary
        }
    ];
}

const BranchLeaseIdentifier = "Branch";
const BuildLeaseIdentifier = "Build";
const RMLeaseIdentifier = "RM";
const UserLeaseIdentifier = "User";
const PipelineLeaseIdentifier = "Pipeline";
